import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { NUMERIC_CELL_SX } from '../../../components/BasicTable/BasicTable';
import { backState } from '../../../components/BackLink';
import StatusChip from '../../../components/StatusChip';
import { MESSAGE_TYPE } from '../../../common/constants/common.constants';
import { createNotification } from '../../../utilities/helper/notification.helper';
import { formatMonthKey, formatSerbianDate } from '../../../utilities/helper/date.helper';
import { InvoiceBatchStatusEnum } from '../../../common/enums/invoicing.enum';
import { InvoiceBatchDto } from '../../../common/interfaces/invoicing.interface';
import { getInvoiceBatches } from '../../../api/invoicing.api';
import { FINANCE } from '../../../common/constants/routes.constants';
import { useTranslation } from 'react-i18next';

import { palette } from '../../../theme';
interface InvoiceBatchesTabProps {
  kindergartenId: string;
}

/**
 * Every generation run, so one can be found again.
 *
 * A run used to be reachable only by the redirect that followed generating it: navigate
 * away with drafts still unissued and there was no way back to them, which is precisely
 * the state somebody leaves a run in when they stop to fix a missing payer.
 */
const InvoiceBatchesTab: React.FC<InvoiceBatchesTabProps> = ({ kindergartenId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [batches, setBatches] = useState<InvoiceBatchDto[]>([]);
  const [loader, setLoader] = useState(true);

  const load = useCallback(async () => {
    setLoader(true);
    try {
      const result = await getInvoiceBatches(kindergartenId);
      setBatches(result.results ?? []);
    } catch {
      createNotification(MESSAGE_TYPE.ERROR, t('invoices.runs.loadFailed'));
    } finally {
      setLoader(false);
    }
  }, [kindergartenId, t]);

  useEffect(() => {
    load();
  }, [load]);

  if (loader) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!batches.length) {
    return (
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        {t('invoices.runs.noneYet')}
      </Alert>
    );
  }

  const unissued = batches.filter(batch => batch.status === InvoiceBatchStatusEnum.DRAFTS_READY);

  return (
    <Box>
      {!!unissued.length && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          {t('invoices.runs.unissuedWarning', { count: unissued.length })}
        </Alert>
      )}

      <Box sx={{ border: `1px solid ${palette.line}`, borderRadius: 4, p: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '22%' }}>{t('invoices.columns.period')}</TableCell>
              <TableCell sx={{ width: '16%' }}>{t('invoices.runs.runOn')}</TableCell>
              <TableCell align="right">{t('invoices.runs.drafts')}</TableCell>
              <TableCell align="right">{t('invoices.runs.issued')}</TableCell>
              <TableCell align="right">{t('invoices.runs.skipped')}</TableCell>
              <TableCell align="right">{t('invoices.runs.failed')}</TableCell>
              <TableCell sx={{ width: '18%' }}>{t('common.status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {batches.map(batch => {
              const open = () =>
                navigate(
                  `${FINANCE}/batch/${batch.id}?kindergartenId=${kindergartenId}`,
                  backState(`${location.pathname}${location.search}`, t('invoices.title'))
                );
              return (
              // the row is the way in; a separate "Open" button was the same click twice
              <TableRow
                key={batch.id}
                hover
                tabIndex={0}
                sx={{ cursor: 'pointer' }}
                onClick={open}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    open();
                  }
                }}
              >
                <TableCell>{formatMonthKey(batch.periodStart.slice(0, 7))}</TableCell>
                <TableCell>{formatSerbianDate(batch.createdAt)}</TableCell>
                <TableCell sx={NUMERIC_CELL_SX}>{batch.createdCount}</TableCell>
                <TableCell sx={NUMERIC_CELL_SX}>{batch.issuedCount}</TableCell>
                <TableCell sx={NUMERIC_CELL_SX}>{batch.skippedCount || '—'}</TableCell>
                <TableCell sx={{ ...NUMERIC_CELL_SX, fontWeight: batch.failedCount ? 700 : 400 }}>{batch.failedCount || '—'}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    <StatusChip kind="batch" status={batch.status} />
                  </Stack>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      <Typography sx={{ mt: 2, fontSize: 13, color: 'text.secondary' }}>{t('invoices.runs.openHint')}</Typography>
    </Box>
  );
};

export default InvoiceBatchesTab;
